import React from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout/Layout';
import stagePasDeDeux from '../assets/stage_pas_de_deux.jpg';
import { getActiveSeason } from '../config/seasons';

const HowToChooseVariation = () => {
  const season = getActiveSeason();
  return (
    <Layout
      title="How to Choose the Right Variation | Prix du Canada"
      description="Coach tips on matching your technique and personality to the perfect competition solo. A guide for ballet dancers preparing for competition."
    >
      {/* Back Navigation */}
      <div style={{ background: 'var(--light-gray)', padding: '1rem 0', borderBottom: '1px solid #e0e0e0' }}>
        <div className="container">
          <Link to="/learn-explore" style={{ color: 'var(--primary-gold)', textDecoration: 'none', fontWeight: '600', fontSize: '0.95rem', display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
            ← Back to Learn & Explore
          </Link>
        </div>
      </div>

      {/* Hero */}
      <section style={{ position: 'relative', height: '320px', overflow: 'hidden' }}>
        <img src={stagePasDeDeux} alt="Ballet on stage" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', textAlign: 'center', padding: '2rem' }}>
          <h1 style={{ color: 'white', marginBottom: '1rem', textShadow: '2px 2px 8px rgba(0,0,0,0.5)' }}>How to Choose the Right Variation</h1>
          <p style={{ color: 'rgba(255,255,255,0.9)', fontSize: '1.2rem', maxWidth: '600px', textShadow: '1px 1px 4px rgba(0,0,0,0.5)' }}>Coach tips on matching your technique and personality to the perfect solo</p>
        </div>
      </section>

      {/* Content */}
      <section className="section" style={{ padding: '4rem 0' }}>
        <div className="container" style={{ maxWidth: '800px' }}>

          {/* Intro */}
          <div style={{ background: 'var(--soft-cream)', borderLeft: '4px solid var(--primary-gold)', padding: '1.5rem 2rem', borderRadius: '0 8px 8px 0', marginBottom: '3rem' }}>
            <p style={{ fontSize: '1.15rem', lineHeight: '1.8', margin: 0, color: 'var(--charcoal)', fontStyle: 'italic' }}>
              Choosing the right variation is one of the most important decisions you'll make as a competing dancer. The perfect variation isn't the hardest one, or the most impressive one — it's the one that makes <em>you</em> shine.
            </p>
          </div>

          {/* Section 1 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>1. Know Your Technical Strengths</h2>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Before browsing the repertoire, take an honest inventory of what your body does best. Every dancer has a physical signature — some have effortlessly high extensions, others have lightning-fast footwork or long, sweeping lines. Great variation selection starts with self-knowledge.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1rem', marginTop: '1.5rem' }}>
              {[
                { title: 'Balance & Adagio', desc: 'Variations with sustained balances, slow adagio phrasing, and held arabesque lines — think Sleeping Beauty, Raymonda' },
                { title: 'Allegro & Footwork', desc: 'Quick variations with beats, intricate bourrées, and fast articulation — think Talisman, Esmeralda, Swanilda' },
                { title: 'Turns', desc: 'If pirouettes and chaîné sequences are your forte, look for variations with diagonal or center turns' },
                { title: 'Jumps', desc: 'Grand allegro variations reward dancers with natural elevation and a buoyant quality' },
              ].map((item, i) => (
                <div key={i} className="card" style={{ padding: '1.25rem', borderTop: '3px solid var(--primary-gold)' }}>
                  <h4 style={{ color: 'var(--primary-gold)', marginBottom: '0.5rem', fontSize: '1rem' }}>{item.title}</h4>
                  <p style={{ fontSize: '0.9rem', lineHeight: '1.5', margin: 0 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Section 2 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>2. Match the Character to Your Personality</h2>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Technique is only half the picture. Judges are watching for a dancer who is genuinely connected to the character they're portraying. A shy dancer forcing a fiery Kitri, or an ebullient performer in the ethereal Shades scene, will feel incongruent no matter how clean the technique.
            </p>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Ask yourself: Do you naturally move with drama and flair, or with dreamlike serenity? Are you drawn to the comic and playful, or the noble and regal? Choose a character you can inhabit authentically — because genuine presence always outscores technically perfect blankness.
            </p>
            <div style={{ background: 'var(--light-gray)', padding: '1.5rem', borderRadius: '8px', marginTop: '1rem' }}>
              <h4 style={{ color: 'var(--charcoal)', marginBottom: '1rem' }}>Quick Character Match Guide</h4>
              <ul style={{ paddingLeft: '1.5rem', lineHeight: '2', margin: 0 }}>
                <li><strong>Dramatic &amp; expressive:</strong> Kitri, Esmeralda, Don Quixote characters</li>
                <li><strong>Ethereal &amp; lyrical:</strong> Kingdom of the Shades, Giselle Act II, Lilac Fairy</li>
                <li><strong>Playful &amp; bright:</strong> Swanilda, Lise (La Fille Mal Gardée), Talisman</li>
                <li><strong>Regal &amp; classical:</strong> Sleeping Beauty, Raymonda, Pharaoh's Daughter</li>
                <li><strong>Romantic &amp; tender:</strong> Giselle Act I, Juliet, La Sylphide</li>
              </ul>
            </div>
          </div>

          {/* Section 3 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>3. Consider Your Training Level</h2>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Reaching above your current level rarely pays off in competition. A variation that's just within your grasp — one you can perform cleanly and confidently — will always score higher than an ambitious piece filled with hesitant moments and near-misses.
            </p>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
              The goal is to look <em>effortless</em>. If you're spending all your mental energy surviving the variation, there's none left for artistry. Pick something that gives you room to perform, not just execute.
            </p>
          </div>

          {/* Section 4 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>4. Think About the Music</h2>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Your relationship with the music is visible. Dancers who genuinely love the score of their variation move differently — with more nuance, more colour, more natural phrasing. If the music doesn't move you in the studio, it won't move the audience on stage.
            </p>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
              Try listening to several variations before deciding. Which score makes you want to move? Which rhythm feels natural in your body? Let that instinct guide you alongside the technical considerations.
            </p>
          </div>

          {/* Section 5 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>5. Talk to Your Coach Early</h2>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem', marginBottom: '1rem' }}>
              Your teacher has watched you move for months or years. They know your strengths, your habits, and your growth trajectory in ways you may not see yourself. Before you fall in love with a variation on YouTube, bring your shortlist to your coach and ask for honest input.
            </p>
            <p style={{ lineHeight: '1.8', fontSize: '1.05rem' }}>
              Ideally, start this conversation <strong>at least three to four months</strong> before the competition. That gives you time to explore the variation, identify challenges, address them in lessons, and build genuine confidence — not just familiarity.
            </p>
          </div>

          {/* Section 6 */}
          <div style={{ marginBottom: '3rem' }}>
            <h2 style={{ color: 'var(--primary-gold)', borderBottom: '2px solid var(--light-gray)', paddingBottom: '0.75rem', marginBottom: '1.5rem' }}>6. Common Mistakes to Avoid</h2>
            <div style={{ display: 'grid', gap: '1rem' }}>
              {[
                { mistake: 'Choosing based on costume alone', fix: 'The costume should come last. Fall in love with the variation, then find a costume that enhances it.' },
                { mistake: 'Picking what your friend is doing', fix: 'Two dancers performing the same variation will be directly compared. Pick something that belongs to you.' },
                { mistake: 'Choosing the "hardest" variation to impress', fix: 'Judges reward mastery. A clean, confident performance of a moderate variation wins over a messy attempt at something too advanced.' },
                { mistake: 'Deciding in a rush', fix: 'Give yourself time. Watch recordings of yourself. Sleep on it. The right choice rarely happens in one afternoon.' },
              ].map((item, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0', borderRadius: '8px', overflow: 'hidden', border: '1px solid #eee' }}>
                  <div style={{ background: '#fff3f3', padding: '1rem', borderRight: '1px solid #eee' }}>
                    <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: '600', color: '#c0392b' }}>✗ {item.mistake}</p>
                  </div>
                  <div style={{ background: '#f0fff4', padding: '1rem' }}>
                    <p style={{ margin: 0, fontSize: '0.95rem', color: '#27ae60' }}>✓ {item.fix}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div style={{ background: 'linear-gradient(135deg, var(--primary-gold), var(--deep-gold))', borderRadius: '12px', padding: '2.5rem', textAlign: 'center', color: 'white' }}>
            <h3 style={{ color: 'white', marginBottom: '1rem' }}>Ready to Find Your Perfect Variation?</h3>
            <p style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '2rem', lineHeight: '1.6' }}>
              Take our quiz to find a variation that suits your style, or register for Prix du Canada {season.year} and take the stage.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <Link to="/quiz" style={{ background: 'white', color: 'var(--primary-gold)', padding: '0.85rem 2rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '700', fontSize: '1rem' }}>Take the Quiz</Link>
              <Link to="/register" style={{ background: 'rgba(255,255,255,0.2)', color: 'white', padding: '0.85rem 2rem', borderRadius: '6px', textDecoration: 'none', fontWeight: '700', fontSize: '1rem', border: '2px solid rgba(255,255,255,0.5)' }}>Register for {season.year}</Link>
            </div>
          </div>

        </div>
      </section>
    </Layout>
  );
};

export default HowToChooseVariation;
